/**
 * Where a payment page sends the customer back to.
 *
 * The payment system returns every SaaS 8 customer to one address
 * (`m_payment_conf.redirect_url` on the Avendor server), whether the purchase
 * was started in the ASEED app or on this website. It used to be the app's
 * `aseed://paypay`, which a browser cannot follow, so web customers were
 * stranded on the payment page (test server, 2026-10-17). It now points to
 * `/app/return`, which decides per customer (operator: "if app hit it it
 * should go back to the app. if website hit it we will return to website"):
 *
 *  - The website marks the hand-off when it sends a customer to a payment page
 *    ("paying": visit id and time). A fresh mark in this browser means the
 *    website sent them: back to that visit, whose screen follows the result
 *    live whatever the payment page reported.
 *  - No fresh mark: the app sent them. On to `aseed://paypay`, with the payment
 *    page's query, exactly as the app received it before.
 *
 * Since the Avendor change of 2026-10-17 a web payment returns with `?rt=`, a
 * one-time token (6 min) minted when the payment was created: the server says
 * whose visit it is, and a link with `rt` is never sent to the app. The mark
 * stays as the fallback for a token that is used up or expired.
 */

/** The Android app's own return address, as the payment config had it. */
export const APP_RETURN = "aseed://paypay";

/** A payment hand-off older than this is not taken as the website's. */
export const HANDOFF_TTL_MS = 30 * 60 * 1000;

export type ReturnTarget =
  | { kind: "token"; token: string }
  | { kind: "website"; visitId: string; path: string }
  | { kind: "app"; href: string };

const RETURN_TOKEN = /^[A-Za-z0-9_-]{16,128}$/;

/** The "paying" mark the website stores before opening a payment page. */
export function handoffMark(visitId: string, now: number = Date.now()): string {
  return `${now}|${visitId}`;
}

/** The visit a fresh mark names, or null for no mark, a stale one or garbage. */
export function handoffVisit(mark: string | null, now: number = Date.now()): string | null {
  if (!mark) return null;
  const at = mark.indexOf("|");
  if (at <= 0) return null;
  const when = Number(mark.slice(0, at));
  const visitId = mark.slice(at + 1);
  if (!Number.isFinite(when) || !visitId) return null;
  const age = now - when;
  return age >= 0 && age <= HANDOFF_TTL_MS ? visitId : null;
}

export function returnTarget(mark: string | null, search: string, now: number = Date.now()): ReturnTarget {
  const rt = new URLSearchParams(search).get("rt");
  if (rt && RETURN_TOKEN.test(rt)) return { kind: "token", token: rt };
  const visitId = handoffVisit(mark, now);
  if (visitId) return { kind: "website", visitId, path: `/visit/${encodeURIComponent(visitId)}` };
  const query = search.startsWith("?") ? search : search ? `?${search}` : "";
  return { kind: "app", href: `${APP_RETURN}${query}` };
}
